import { stateful } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';

type LoaderSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type LoaderType = 'spinner' | 'ring' | 'dots' | 'infinity' | 'bars';
type LoaderColor = 'primary' | 'secondary' | 'accent' | 'neutral' | 'info' | 'success' | 'warning' | 'error';

interface LoaderOptions {
  size?: LoaderSize;
  type?: LoaderType;
  opacity?: string;
  color?: LoaderColor;
  message?: string;
}

/**
 * A subtle full-page loader component
 * @param options Configuration options for the loader
 * @returns A functional component that renders a full-page loader
 */
export const FullPageLoader = stateful((options: LoaderOptions = {
  size: 'lg', // xs, sm, md, lg, xl
  type: 'spinner', // spinner, ring, dots, infinity, bars
  opacity: '40', // 0-100 opacity percentage
  color: 'primary', // primary, secondary, accent, neutral, info, success, warning, error
}) => {
  const { 
    size = 'lg', 
    type = 'spinner', 
    opacity = '40', 
    color = 'primary' 
  } = options;
  
  return html`
    <div class="min-h-dvh w-full flex items-center justify-center bg-base-100/50">
      <span class="loading loading-${type} loading-${size} text-${color}/${opacity}"></span>
    </div>
  `;
});

/**
 * A simple full-page loader with default settings
 */
export const DefaultFullPageLoader = () => FullPageLoader({});

/**
 * A container loader component for use in pages or sections
 * @param options Configuration options for the loader
 * @returns A functional component that renders a container loader
 */
export const ContainerLoader = stateful((options: LoaderOptions = {
  size: 'lg',
  type: 'spinner',
  opacity: '60',
  color: 'primary',
  message: '',
}) => {
  const { 
    size = 'lg', 
    type = 'spinner', 
    opacity = '60', 
    color = 'primary',
    message = ''
  } = options;
  
  return html`
    <div class="p-8 flex flex-col items-center justify-center">
      <span class="loading loading-${type} loading-${size} text-${color}/${opacity}"></span>
      ${message ? html`<p class="mt-4 text-base-content/70">${message}</p>` : ''}
    </div>
  `;
});

/**
 * A simple container loader with default settings
 */
export const DefaultContainerLoader = () => ContainerLoader({});

/**
 * A container loader with a message
 */
export const MessagedContainerLoader = (message: string) => ContainerLoader({ message });
